import { Input } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import React, { useState, CSSProperties } from 'react';
import { TextArea } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';

import {
  TimerEntity,
  ResourceEntity
} from '../../../types/game-document/entities';
import { Algorithm } from '../../../types/algorithm';
import { AlgorithmStep } from '../../../types/algorithm';
import RequiredFields from '../../../types/required-fields';
import { AlgorithmEditor } from '../../../components/algorithm-editor';
import { TimerEvent } from '../../../types/game-document/entities/timer';
import { ResourceType } from '../../../types/game-document/resource-type';
import { StandardInput } from '../../../components/forms';

interface TimerEditorProps {
  status?: string;
  timer?: TimerEntity;
  resources: ResourceEntity[];
  requiredFields?: RequiredFields<TimerEntity>[];
  handleEntityChange?: (
    field: keyof TimerEntity,
    value: string | number,
    type: ResourceType,
    isClearValue?: boolean
  ) => void;
  handleEventAction?: (task: TimerEntity) => void;
}

interface TimerEditorProps {
  timer?: TimerEntity;
  resources: ResourceEntity[];
  requiredFields?: RequiredFields<TimerEntity>[];
  handleEntityChange?: (
    field: keyof TimerEntity,
    value: string | number,
    type: ResourceType,
    isClearValue?: boolean
  ) => void;
  handleEventAction?: (task: TimerEntity) => void;
}

const DURATION_UNITS = ['Seconds', 'Minutes', 'Hours'];
const COUNT_DIRECTIONS = [
  { text: 'Counts down', value: 'down' },
  { text: 'Counts up', value: 'up' }
];
const REPEAT_OPTIONS = [
  { text: 'Does not repeat', value: false },
  { text: 'Repeats', value: true }
];

const AlgorithmContainer: React.FC<{
  title: string;
  action: string;
  subtitle: string;
  algorithm?: Algorithm;
  type: 'start' | 'elapsed' | 'stop';
  onChange?: (algorithm: Algorithm) => void;
}> = ({ type, action, algorithm, subtitle, title, onChange }) => {
  const bgColor = React.useMemo(() => {
    if (type === 'start') return '#14BF8B33';
    if (type === 'elapsed') return '#4698CB1A';

    return '#D4273326';
  }, [type]);

  const handleOnChange = (steps: Array<AlgorithmStep>) => {
    let gameSteps: Algorithm = { ...algorithm };
    gameSteps.steps = steps;

    if (onChange) onChange(gameSteps);
  };

  return (
    <div
      className={'d-flex flex-column'}
      style={{
        padding: '16px',
        gap: '16px',
        borderRadius: '8px',
        backgroundColor: bgColor
      }}>
      <div className={'d-flex flex-column'}>
        <span
          style={{
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
          {title}
        </span>
        <span
          style={{
            fontSize: '14px',
            color: '#6c757d'
          }}>
          {subtitle}
        </span>
      </div>
      <AlgorithmEditor
        action={action}
        shortcutText={'What happens'}
        additionalClassname={`${type}-timer`}
        steps={algorithm?.steps ?? []}
        onChange={handleOnChange}
      />
    </div>
  );
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: '#4a4a4a',
  marginBottom: 4
};

export const TimerEditorFull = ({
  timer,
  resources,
  requiredFields,
  handleEventAction = () => {},
  handleEntityChange = () => {}
}: TimerEditorProps) => {
  const [durationUnit, setDurationUnit] = useState<string>(DURATION_UNITS[0]);

  const getTimerInterval = React.useMemo(() => {
    const interval = timer?.interval ?? 0;
    if (durationUnit === 'Minutes') return interval / 60;

    if (durationUnit === 'Hours') return interval / 3600;

    return interval;
  }, [timer?.interval, durationUnit]);

  const eventActionHandler = (type: keyof TimerEvent, algorithm: Algorithm) => {
    let newTimer: TimerEntity = { ...timer! };
    if (!newTimer.events) {
      newTimer.events = {};
    }
    newTimer.events[type] = algorithm;
    handleEventAction(newTimer);
  };

  const handleEventOnStartChange = (algorithm: Algorithm) => {
    eventActionHandler('onStartTimer', algorithm);
  };

  const handleEventOnStopChange = (algorithm: Algorithm) => {
    eventActionHandler('onStopTimer', algorithm);
  };

  const handleEventOnElapsedChange = (algorithm: Algorithm) => {
    eventActionHandler('onElapsedTimer', algorithm);
  };

  const onChangeTimerHandler = (field: keyof TimerEntity, value: string) => {
    handleEntityChange(field, value, 'text');
  };

  return (
    <div
      className={'d-flex flex-column'}
      style={{
        gap: '24px'
      }}>
      <div>
        <p style={{ fontWeight: 600, margin: '0 0 2px' }}>Timer info</p>
        <p style={{ fontSize: 14, color: '#6c757d', margin: '0 0 14px' }}>
          Configure the timers settings and events
        </p>

        <div style={{ width: '100%', marginBottom: 14 }}>
          <label style={labelStyle}>Timer Title</label>
          <Input
            name={'titleResId'}
            placeholder="Timer Title"
            value={
              resources.find((item) => item?.id === timer?.titleResId)?.value ??
              ''
            }
            onChange={(e: InputChangeEvent) => {
              handleEntityChange('titleResId', e.value, 'text');
            }}
            validationMessage={
              requiredFields?.find((item) => item.name === 'titleResId')
                ?.errorMessage
            }
          />
          {requiredFields?.find((item) => item.name === 'titleResId')
            ?.errorMessage !== '' && (
            <Error>
              {
                requiredFields?.find((item) => item.name === 'titleResId')
                  ?.errorMessage
              }
            </Error>
          )}
        </div>

        <label style={labelStyle}>Timer Description</label>
        <TextArea
          placeholder="Timer Description"
          value={timer?.description}
          onChange={(e) => {
            handleEntityChange('description', e.value, 'text');
          }}
          style={{ width: '100%', marginBottom: 14 }}
        />

        {/* Duration row */}
        <label style={labelStyle}>Duration</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className={'d-flex'}>
            <Input
              placeholder="000"
              value={getTimerInterval}
              onChange={(e: InputChangeEvent) => {
                let value = parseInt(e.value === '' ? '0' : e.value);
                if (durationUnit === 'Minutes') value = value * 60;
                if (durationUnit === 'Hours') value = value * 3600;

                onChangeTimerHandler('interval', value.toString());
              }}
              style={{
                width: 100,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0
              }}
            />
            <DropDownList
              data={DURATION_UNITS}
              value={durationUnit}
              onChange={(e: DropDownListChangeEvent) =>
                setDurationUnit(e.value)
              }
              style={{
                width: 120,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0
              }}
            />
          </div>
          <DropDownList
            data={COUNT_DIRECTIONS}
            textField={'text'}
            dataItemKey={'value'}
            value={COUNT_DIRECTIONS.find((c) => c.value === timer?.direction)}
            onChange={(e: DropDownListChangeEvent) =>
              onChangeTimerHandler('direction', e.value.value)
            }
            style={{ width: 150 }}
          />
          <DropDownList
            data={REPEAT_OPTIONS}
            textField={'text'}
            dataItemKey={'value'}
            value={REPEAT_OPTIONS.find((r) => r.value === timer?.repeats)}
            onChange={(e: DropDownListChangeEvent) =>
              onChangeTimerHandler('repeats', e.value.value)
            }
            style={{ width: 180 }}
          />
        </div>
      </div>

      <div className={'d-flex flex-column gap-2'}>
        <div>
          <p style={{ fontWeight: 600, margin: '0 0 2px' }}>Timer events</p>
          <p style={{ fontSize: 14, color: '#6c757d', margin: 0 }}>
            Now configure this timer's events. You don't need to use all three
            timer events. Configure the ones you need and leave the rest empty.
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16
          }}>
          <AlgorithmContainer
            action={'Timer.onStartTimer'}
            type={'start'}
            algorithm={timer?.events?.onStartTimer}
            title={'What happens when the timer starts?'}
            subtitle={'Configure what happens when the timer starts.'}
            onChange={handleEventOnStartChange}
          />
          <AlgorithmContainer
            action={'Timer.onElapsedTimer'}
            type={'elapsed'}
            algorithm={timer?.events?.onElapsedTimer}
            title={'What happens when the timer runs out?'}
            subtitle={
              'Configure what happens when the timer reaches its limit.'
            }
            onChange={handleEventOnElapsedChange}
          />
          <AlgorithmContainer
            action={'Timer.onStopTimer'}
            algorithm={timer?.events?.onStopTimer}
            title={'What happens when the timer is stopped?'}
            subtitle={
              'Configure what happens when the timer is stopped before it reaches its limit.'
            }
            type={'stop'}
            onChange={handleEventOnStopChange}
          />
        </div>
      </div>
    </div>
  );
};
